import { UserRepository } from '@/store/user-state/user-state.repository';
import { DynamicForm, FieldVariant, SelectOption } from '../../../../components/form-builder/form-builder.types';
import { map } from 'rxjs/operators';
import { DynamicField } from '../../../../components/form-builder/form-builder.types';
import { Observable } from 'rxjs';
import { combineLatest } from 'rxjs';

interface getUserFormObjectProps {
  store: UserRepository;
  rolesOptions?: SelectOption[];
  acsOptions?: SelectOption[];
  comsOptions?: SelectOption[];
}

export const getUserFormObject = ({ store, rolesOptions, acsOptions, comsOptions }: getUserFormObjectProps): DynamicForm => {
  const getShowError = (touched: boolean, submitted: boolean) => touched || submitted;
  const usernameField: DynamicField = {
    label: 'settings.user.form.username.label',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: store.getNestedObservable('isDisabled', 'username') as Observable<boolean>,
    error: combineLatest([
      store.getObservable('usernameTouched'),
      store.getObservable('formSubmitted'),
      store.getNestedObservable('dto', 'username'),
    ]).pipe(
      map(([touched, submitted, value]) => {
        if ((touched || submitted) && !value) return "Le nom d'utilisateur est requis";
        if ((touched || submitted) && typeof value === 'string' && value.includes(' ')) return "Le nom d'utilisateur ne doit pas contenir d'espaces";
        return '';
      })
    ),
    props: {
      value: store.getNestedObservable('dto', 'username'),
      onChange: (value: string) => {
        store.setNested('dto', 'username', value);
      },
      onBlur: () => store.set('usernameTouched', true),
      placeholder: "Nom d'utilisateur",
    },
  };
  const firstNameField: DynamicField = {
    label: 'settings.user.form.firstName.label',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: store.getNestedObservable('isDisabled', 'firstName') as Observable<boolean>,
    error: combineLatest([
      store.getObservable('firstNameTouched'),
      store.getObservable('formSubmitted'),
      store.getNestedObservable('dto', 'firstName'),
    ]).pipe(
      map(([touched, submitted, value]) => {
        if ((touched || submitted) && !value) return 'Le prénom est requis';
        return '';
      })
    ),
    props: {
      value: store.getNestedObservable('dto', 'firstName'),
      onChange: (value: string) => {
        store.setNested('dto', 'firstName', value);
      },
      onBlur: () => store.set('firstNameTouched', true),
      placeholder: 'Prénom',
    },
  };
  const lastNameField: DynamicField = {
    label: 'settings.user.form.lastName.label',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: store.getNestedObservable('isDisabled', 'lastName') as Observable<boolean>,
    error: combineLatest([
      store.getObservable('lastNameTouched'),
      store.getObservable('formSubmitted'),
      store.getNestedObservable('dto', 'lastName'),
    ]).pipe(
      map(([touched, submitted, value]) => {
        if ((touched || submitted) && !value) return 'Le nom est requis';
        return '';
      })
    ),
    props: {
      value: store.getNestedObservable('dto', 'lastName'),
      onChange: (value: string) => {
        store.setNested('dto', 'lastName', value);
      },
      onBlur: () => store.set('lastNameTouched', true),
      placeholder: 'Nom',
    },
  };
  const emailField: DynamicField = {
    label: 'settings.user.form.email.label',
    variant: FieldVariant.EMAIL,
    isRequired: true,
    isDisabled: store.getNestedObservable('isDisabled', 'email') as Observable<boolean>,
    error: combineLatest([store.getObservable('emailTouched'), store.getObservable('formSubmitted'), store.getNestedObservable('dto', 'email')]).pipe(
      map(([touched, submitted, value]) => {
        const email = typeof value === 'string' ? value : '';
        if ((touched || submitted) && !email) return "L'email est requis";
        if ((touched || submitted) && email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Format email invalide';
        return '';
      })
    ),
    props: {
      value: store.getNestedObservable('dto', 'email'),
      onChange: (value: string) => {
        store.setNested('dto', 'email', value);
      },
      onBlur: () => store.set('emailTouched', true),
      placeholder: 'Email',
    },
  };
  const rolesField = {
    label: 'settings.user.form.roles.label',
    variant: FieldVariant.MULTISELECT,
    isRequired: true,
    isDisabled: store.getNestedObservable('isDisabled', 'roles') as Observable<boolean>,
    props: {
      options: rolesOptions || [],
      value: (store.getNestedObservable('dto', 'roles') as Observable<string[] | undefined>).pipe(
        map((selectedRoleCodes: string[] | undefined) => {
          const optionsArr: SelectOption[] = Array.isArray(rolesOptions) ? rolesOptions : [];
          return (selectedRoleCodes || [])
            .map(code => {
              return optionsArr.find((option: SelectOption) => option.code === code);
            })
            .filter(Boolean) as SelectOption[];
        })
      ),
      onSelectChange: (value: SelectOption[]) => {
        const codes = value.map(v => v.code as string);
        store.setNested('dto', 'roles', codes);
      },
      isMulti: true,
      placeholder: 'Sélectionnez un ou plusieurs rôles',
    },
  };
  const acsField = {
    label: 'settings.user.form.acs.label',
    variant: FieldVariant.MULTISELECT,
    isRequired: false,
    isDisabled: store.getNestedObservable('isDisabled', 'acs') as Observable<boolean>,
    props: {
      options: acsOptions || [],
      value: store.getNestedObservable('dto', 'acIds').pipe(
        map((ids: unknown) => {
          const idArray = (ids as number[]) || [];
          return (acsOptions || []).filter(opt => idArray.includes(Number(opt.code)));
        })
      ),
      onSelectChange: (value: any[]) => {
        const codes = (value || []).map(v => (typeof v === 'object' && v !== null ? Number(v.code) : Number(v)));
        store.setNested('dto', 'acIds', codes);
        const selectedAcs = (value || []).map(v => ({
          name: typeof v === 'object' && v !== null ? v.name : '',
          code: typeof v === 'object' && v !== null ? v.code : v,
        }));
        store.set('selectedAcs', selectedAcs);
      },
      placeholder: 'Sélectionnez une ou plusieurs AC',
    },
  };
  const acPiloteField: DynamicField = {
    label: 'settings.user.form.acPilote.label',
    variant: FieldVariant.SELECT,
    isRequired: false,
    isDisabled: store.getNestedObservable('isDisabled', 'acPilote') as Observable<boolean>,
    props: {
      options: Array.isArray(store.get('selectedAcs'))
        ? (store.get('selectedAcs') as SelectOption[]).map((ac: SelectOption) => ({ name: ac.name, code: ac.code }))
        : [],
      value: combineLatest([store.getNestedObservable('dto', 'acPiloteId'), store.getObservable('selectedAcs')]).pipe(
        map(([acPiloteId, selectedAcs]) => {
          if (typeof acPiloteId !== 'number' || !selectedAcs) return undefined;
          const selectedAc = (selectedAcs as SelectOption[]).find(opt => opt.code === acPiloteId);
          return selectedAc ? { name: selectedAc.name, code: selectedAc.code } : undefined;
        })
      ),
      onSelectChange: (value: any) => {
        if (value) {
          const code = Number(value.code);
          store.setNested('dto', 'acPiloteId', code);
          store.set('acPiloteSelected', { name: value.name, code });
        } else {
          store.setNested('dto', 'acPiloteId', undefined);
          store.set('acPiloteSelected', undefined);
        }
      },
      placeholder: 'Sélectionnez le AC principal',
    },
  };
  const comsField = {
    label: 'settings.user.form.coms.label',
    variant: FieldVariant.MULTISELECT,
    isRequired: false,
    isDisabled: store.getNestedObservable('isDisabled', 'coms') as Observable<boolean>,
    props: {
      options: comsOptions || [],
      value: store.getNestedObservable('dto', 'comIds'),
      onSelectChange: (value: string[]) => {
        store.setNested('dto', 'comIds', value);
      },
      placeholder: 'Sélectionnez une ou plusieurs COM',
    },
  };
  const addressField: DynamicField = {
    label: 'settings.user.form.address.label',
    variant: FieldVariant.TEXT,
    isRequired: false,
    isDisabled: store.getNestedObservable('isDisabled', 'address') as Observable<boolean>,
    error: combineLatest([
      store.getObservable('addressTouched'),
      store.getObservable('formSubmitted'),
      store.getNestedObservable('dto', 'address'),
    ]).pipe(
      map(([touched, submitted, value]) => {
        if ((touched || submitted) && !value) return '';
        return '';
      })
    ),
    props: {
      value: store.getNestedObservable('dto', 'address'),
      onChange: (value: string) => {
        store.setNested('dto', 'address', value);
      },
      onBlur: () => store.set('addressTouched', true),
      placeholder: 'Adresse',
    },
  };
  const mobileField: DynamicField = {
    label: 'settings.user.form.mobile.label',
    variant: FieldVariant.TEL,
    isRequired: false,
    isDisabled: store.getNestedObservable('isDisabled', 'mobile') as Observable<boolean>,
    error: combineLatest([
      store.getObservable('mobileTouched'),
      store.getObservable('formSubmitted'),
      store.getNestedObservable('dto', 'mobile'),
    ]).pipe(
      map(([touched, submitted, value]) => {
        if ((touched || submitted) && !value) return '';
        return '';
      })
    ),
    props: {
      value: store.getNestedObservable('dto', 'mobile'),
      onChange: (value: string) => {
        store.setNested('dto', 'mobile', value);
      },
      onBlur: () => store.set('mobileTouched', true),
      placeholder: 'Mobile',
    },
  };
  const fixeField: DynamicField = {
    label: 'settings.user.form.fixe.label',
    variant: FieldVariant.TEL,
    isRequired: false,
    isDisabled: store.getNestedObservable('isDisabled', 'fixe') as Observable<boolean>,
    error: combineLatest([store.getObservable('fixeTouched'), store.getObservable('formSubmitted'), store.getNestedObservable('dto', 'fixe')]).pipe(
      map(([touched, submitted, value]) => {
        if ((touched || submitted) && !value) return '';
        return '';
      })
    ),
    props: {
      value: store.getNestedObservable('dto', 'fixe'),
      onChange: (value: string) => {
        store.setNested('dto', 'fixe', value);
      },
      onBlur: () => store.set('fixeTouched', true),
      placeholder: 'Fixe',
    },
  };
  return {
    title: 'User',
    description: '',
    isHeaderHidden: true,
    grids: [
      {
        title: '',
        isHeaderHidden: true,
        gridItems: [
          { fields: [usernameField, emailField] },
          { fields: [lastNameField, firstNameField] },
          { fields: [mobileField, fixeField] },
          { fields: [addressField] },
          { fields: [rolesField, acsField] },
          { fields: [acPiloteField, comsField] },
        ],
      },
    ],
  };
};

export const getUpdateUserFormObject = ({ store, rolesOptions, acsOptions, comsOptions }: getUserFormObjectProps): DynamicForm => {
  const usernameField: DynamicField = {
    label: 'settings.user.form.username.label',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: store.getNestedObservable('isDisabled', 'username') as Observable<boolean>,
    error: combineLatest([
      store.getObservable('usernameTouched'),
      store.getObservable('formSubmitted'),
      store.getNestedObservable('dto', 'username'),
    ]).pipe(
      map(([touched, submitted, value]) => {
        if ((touched || submitted) && !value) return "Le nom d'utilisateur est requis";
        return '';
      })
    ),
    props: {
      value: store.getNestedObservable('dto', 'username'),
      onChange: (value: string) => {
        store.setNested('dto', 'username', value);
      },
      onBlur: () => store.set('usernameTouched', true),
      placeholder: "Nom d'utilisateur",
    },
  };
  const firstNameField: DynamicField = {
    label: 'settings.user.form.firstName.label',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: store.getNestedObservable('isDisabled', 'firstName') as Observable<boolean>,
    error: combineLatest([
      store.getObservable('firstNameTouched'),
      store.getObservable('formSubmitted'),
      store.getNestedObservable('dto', 'firstName'),
    ]).pipe(
      map(([touched, submitted, value]) => {
        if ((touched || submitted) && !value) return 'Le prénom est requis';
        return '';
      })
    ),
    props: {
      value: store.getNestedObservable('dto', 'firstName'),
      onChange: (value: string) => {
        store.setNested('dto', 'firstName', value);
      },
      onBlur: () => store.set('firstNameTouched', true),
      placeholder: 'Prénom',
    },
  };
  const lastNameField: DynamicField = {
    label: 'settings.user.form.lastName.label',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: store.getNestedObservable('isDisabled', 'lastName') as Observable<boolean>,
    error: combineLatest([
      store.getObservable('lastNameTouched'),
      store.getObservable('formSubmitted'),
      store.getNestedObservable('dto', 'lastName'),
    ]).pipe(
      map(([touched, submitted, value]) => {
        if ((touched || submitted) && !value) return 'Le nom est requis';
        return '';
      })
    ),
    props: {
      value: store.getNestedObservable('dto', 'lastName'),
      onChange: (value: string) => {
        store.setNested('dto', 'lastName', value);
      },
      onBlur: () => store.set('lastNameTouched', true),
      placeholder: 'Nom',
    },
  };
  const emailField: DynamicField = {
    label: 'settings.user.form.email.label',
    variant: FieldVariant.EMAIL,
    isRequired: true,
    isDisabled: store.getNestedObservable('isDisabled', 'email') as Observable<boolean>,
    error: combineLatest([store.getObservable('emailTouched'), store.getObservable('formSubmitted'), store.getNestedObservable('dto', 'email')]).pipe(
      map(([touched, submitted, value]) => {
        const email = typeof value === 'string' ? value : '';
        if ((touched || submitted) && !email) return "L'email est requis";
        if ((touched || submitted) && email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Format email invalide';
        return '';
      })
    ),
    props: {
      value: store.getNestedObservable('dto', 'email'),
      onChange: (value: string) => {
        store.setNested('dto', 'email', value);
      },
      onBlur: () => store.set('emailTouched', true),
      placeholder: 'Email',
    },
  };
  const addressField: DynamicField = {
    label: 'settings.user.form.address.label',
    variant: FieldVariant.TEXT,
    isRequired: false,
    isDisabled: store.getNestedObservable('isDisabled', 'address') as Observable<boolean>,
    error: combineLatest([
      store.getObservable('addressTouched'),
      store.getObservable('formSubmitted'),
      store.getNestedObservable('dto', 'address'),
    ]).pipe(
      map(([touched, submitted, value]) => {
        if ((touched || submitted) && !value) return '';
        return '';
      })
    ),
    props: {
      value: store.getNestedObservable('dto', 'address'),
      onChange: (value: string) => {
        store.setNested('dto', 'address', value);
      },
      onBlur: () => store.set('addressTouched', true),
      placeholder: 'Adresse',
    },
  };
  const mobileField: DynamicField = {
    label: 'settings.user.form.mobile.label',
    variant: FieldVariant.TEL,
    isRequired: false,
    isDisabled: store.getNestedObservable('isDisabled', 'mobile') as Observable<boolean>,
    error: combineLatest([
      store.getObservable('mobileTouched'),
      store.getObservable('formSubmitted'),
      store.getNestedObservable('dto', 'mobile'),
    ]).pipe(
      map(([touched, submitted, value]) => {
        if ((touched || submitted) && !value) return '';
        return '';
      })
    ),
    props: {
      value: store.getNestedObservable('dto', 'mobile'),
      onChange: (value: string) => {
        store.setNested('dto', 'mobile', value);
      },
      onBlur: () => store.set('mobileTouched', true),
      placeholder: 'Mobile',
    },
  };
  const fixeField: DynamicField = {
    label: 'settings.user.form.fixe.label',
    variant: FieldVariant.TEL,
    isRequired: false,
    isDisabled: store.getNestedObservable('isDisabled', 'fixe') as Observable<boolean>,
    error: combineLatest([store.getObservable('fixeTouched'), store.getObservable('formSubmitted'), store.getNestedObservable('dto', 'fixe')]).pipe(
      map(([touched, submitted, value]) => {
        if ((touched || submitted) && !value) return '';
        return '';
      })
    ),
    props: {
      value: store.getNestedObservable('dto', 'fixe'),
      onChange: (value: string) => {
        store.setNested('dto', 'fixe', value);
      },
      onBlur: () => store.set('fixeTouched', true),
      placeholder: 'Fixe',
    },
  };
  return {
    title: 'User',
    description: '',
    isHeaderHidden: true,
    grids: [
      {
        title: '',
        isHeaderHidden: true,
        gridItems: [
          { fields: [usernameField, emailField] },
          { fields: [lastNameField, firstNameField] },
          { fields: [mobileField, fixeField] },
          { fields: [addressField] },
        ],
      },
    ],
  };
};
